/* istanbul ignore file */
/**
 * Created by rathompson on 7/25/22
 * File: WebTableCell
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { Body, Button, Checkbox } from '@panwds/react-ui'
import { Line } from '../../../../../../common/assets/images'
import { RowInstance, TableInstance, useTableInstance } from '@panwds/react-table'
import { EyeIcon, EyeSlashIcon, CircleCheckIcon } from '@panwds/icons'
import styled from 'styled-components'
import { useCallback, useMemo, VFC } from 'react'
import { ACCESS_LEVELS } from '../../../../../constants/IAMConstants'
import { ARRAY } from '../../../../../../common/utils/Utils'
import RowExpansionToggle from '../../../../common/permissionsTable/RowExpansionToggle'
import type { UseRowStateLocalState } from 'react-table'
import type { IAM_PermissionSet } from '../../../../../../common/reducers/types/IAMReducer.types'

const CheckboxWrapper = styled.div`
  padding-right: 1rem;

  label {
    margin: 0;
  }
`

const WebTableCell: VFC<{
  row: RowInstance<IAM_PermissionSet>,
  value: any,
  onCheck: Function,
  onPermissionToggle: Function,
  viewOnly?: boolean
}> = ({ row, value, onCheck, onPermissionToggle, viewOnly = false }) => {

  const tableRef = useTableInstance() as TableInstance<IAM_PermissionSet>

  const icon = () => {
    const { access_level } = (row?.state || {})
    switch (access_level) {
      case ACCESS_LEVELS.READ:
        return <EyeIcon className='tw-text-icon-blue'/>
      case ACCESS_LEVELS.WRITE:
        return <CircleCheckIcon className='tw-text-icon-green'/>
      case ACCESS_LEVELS.NONE:
      default:
        return <EyeSlashIcon className='tw-text-icon-red'/>
    }
  }

  const onClick = useCallback(() => {
    if (viewOnly) {
      return
    }
    onPermissionToggle(row)
  }, [onPermissionToggle, row, viewOnly])

  const onChange = useCallback(() => {
    if (viewOnly) {
      return
    }
    onCheck(row)
  }, [onCheck, row, viewOnly])

  const isParentSelected = useMemo(() => {
    if (row?.original?.parent_row) {
      const parent = tableRef?.flatRows?.find(r => r?.original?.name === row?.original?.parent_row?.name)
      if (parent) {
        return !!tableRef?.state?.selectedRowIds[parent?.id]
      }
    }
    return false
  }, [row, tableRef])

  const [countNone, countRead, countWrite] = useMemo(() => {
    let countNone = 0
    let countRead = 0
    let countWrite = 0

    const counter = (subRow: any) => {
      const state: { cellState: UseRowStateLocalState<IAM_PermissionSet> } | undefined = tableRef?.state?.rowState[subRow.id]
      if (state) {
        const { access_level } = state as unknown as { access_level: string }
        if (access_level === ACCESS_LEVELS.WRITE) {
          countWrite += 1
        }
        else if (access_level === ACCESS_LEVELS.READ) {
          countRead += 1
        }
        else if (access_level === ACCESS_LEVELS.NONE || !access_level) {
          countNone += 1
        }
      }
      else {
        countNone += 1
      }
      ARRAY.hasArray(tableRef?.rowsById?.[subRow.id]?.subRows) && tableRef?.rowsById?.[subRow.id]?.subRows.forEach(counter)
    }

    ARRAY.hasArray(row?.subRows) && row.subRows.forEach(counter)
    return [countNone, countRead, countWrite]
  }, [row, tableRef])

  const suffix = useMemo(() => {
    if (!countNone && !countRead && !countWrite) {
      return null
    }
    const strings = []
    if (countNone) {
      strings.push(`No access ${countNone}`)
    }
    if (countRead) {
      strings.push(`Read only ${countRead}`)
    }
    if (countWrite) {
      strings.push(`Read write ${countWrite}`)
    }
    return `(${strings.join(', ')})`
  }, [countNone, countRead, countWrite])

  const className = 'tw-flex tw-items-center'
  const padding = 20 * row?.depth
  return (
    <div className={className}>
      {!viewOnly && (
        <CheckboxWrapper>
          <Checkbox disabled={isParentSelected} checked={row?.isSelected} onChange={onChange}/>
        </CheckboxWrapper>
      )}
      <div className={className} style={{ paddingLeft: `${padding}px` }}>
        {!row?.canExpand && <div style={{ width: '15px' }}/>}
        {!!row?.depth && <img style={{ width: '10px', height: '10px' }} src={Line} alt={'line'} />}
        {!!row?.canExpand && <RowExpansionToggle row={row}/>}
        <Button
          aria-label='permission-set-access'
          appearance='clear'
          style={{ background: 'transparent' }}
          icon={icon()}
          onClick={onClick}
        />
        <div className='tw-flex tw-gap-2'>
          <Body size='lg'>{row?.original.display_name || row?.original.name}</Body>
          {!!suffix && (<Body size='lg' addClassName='tw-text-secondary'>{suffix}</Body>)}
        </div>
      </div>
    </div>
  )
}

WebTableCell.propTypes = {}
export default WebTableCell
